import { FC, useMemo, useRef, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import {
  TextFieldProps,
  TextField as MuiTextField,
  InputAdornment,
  IconButton
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

type IFormTextInputProps = {
  name: string;
  label: string;
  fieldColor?: "white" | "black";
} & TextFieldProps;

/**
 * Custom text TextField for app forms
 *
 * @param {string} name - Field name
 * @param {string} label - Text field label
 * @param {string} helperText - Text field helper text
 * @param {TextFieldProps} otherProps - Other props from MUI TextField
 */

export const TextField: FC<IFormTextInputProps> = ({
  name,
  label,
  helperText,
  fieldColor = "white",
  type,
  ...otherProps
}) => {
  const { control, formState: { errors } } = useFormContext();
  const fieldRef = useRef<any>();
  const [showPassword, setShowPassword] = useState(false);

  const isPasswordType = type === "password";

  const textFieldType = useMemo(() => {
    if (isPasswordType){
      return showPassword ? "text" : "password"
    }

    return type ?? "text"
  }, [isPasswordType, showPassword, type])

  const sxAttribute: any = useMemo(() => {
    if (fieldColor === "white") return {
      '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        '& fieldset': {
          borderColor: 'transparent',
        },
        '&:hover fieldset': {
          borderColor: 'transparent',
        },
        '&.Mui-focused fieldset': {
          borderColor: 'transparent',
        },
      },
      '& .MuiInputBase-input': {
        color: "white",
        '&::placeholder': {
          color: 'white',
          opacity: 1,
        },
      },
      '& .MuiInputLabel-root': {
        color: 'white',
        '&.Mui-focused': {
          color: 'white',
        },
      },
      '& .MuiFormLabel-filled': {
        color: 'white',
      },
      '& .MuiIconButton-root': {
        color: 'white',
      },
    };
    else return {};
  }, [fieldColor]);

  return (
    <Controller
      control={control}
      name={name}
      defaultValue=""
      render={({ field }) => (
        <MuiTextField
          {...field}
          ref={fieldRef}
          fullWidth
          label={label}
          sx={sxAttribute}
          error={!!errors[name]}
          type={textFieldType}
          helperText={errors[name] ? (errors[name]?.message as unknown as string) : helperText}
          InputProps={{
            endAdornment: isPasswordType ? (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword((prev) => !prev)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          {...otherProps}
        />
      )}
    />
  );
};
